import { HttpClient, NonNullablePaths } from '@wix/sdk-types';
import {
  BulkUpdatePayinsConfigurationTagsByFilterOptions,
  BulkUpdatePayinsConfigurationTagsByFilterResponse,
  BulkUpdatePayinsConfigurationTagsOptions,
  BulkUpdatePayinsConfigurationTagsResponse,
  PayinsConfiguration,
  PayinsConfigurationQuery,
  PayinsConfigurationsQueryBuilder,
  QueryPayinsConfigurationsResponse,
  UpdatePayinsConfiguration,
  bulkUpdatePayinsConfigurationTags as universalBulkUpdatePayinsConfigurationTags,
  bulkUpdatePayinsConfigurationTagsByFilter as universalBulkUpdatePayinsConfigurationTagsByFilter,
  createPayinsConfiguration as universalCreatePayinsConfiguration,
  getPayinsConfiguration as universalGetPayinsConfiguration,
  queryPayinsConfigurations as universalQueryPayinsConfigurations,
  typedQueryPayinsConfigurations as universalTypedQueryPayinsConfigurations,
  updatePayinsConfiguration as universalUpdatePayinsConfiguration,
} from './payments-payins-configurations-v1-payins-configuration-payins-configurations.universal.js';

export const __metadata = { PACKAGE_NAME: '@wix/payments' };

/** @internal */
export function createPayinsConfiguration(
  httpClient: HttpClient
): CreatePayinsConfigurationSignature {
  return (payinsConfiguration: PayinsConfiguration) =>
    universalCreatePayinsConfiguration(
      payinsConfiguration,
      // @ts-ignore
      { httpClient }
    );
}

interface CreatePayinsConfigurationSignature {
  /**
   * Creates a PayinsConfiguration.
   * @param - PayinsConfiguration to be created.
   * @returns The created PayinsConfiguration.
   */
  (payinsConfiguration: PayinsConfiguration): Promise<
    NonNullablePaths<
      PayinsConfiguration,
      | `rules`
      | `rules.${number}.paymentMethodTypeId`
      | `rules.${number}.accountConnectionId`
      | `rules.${number}.merchantSelection.payinsEnabled`
      | `tags.privateTags.tagIds`,
      5
    >
  >;
}

/** @internal */
export function getPayinsConfiguration(
  httpClient: HttpClient
): GetPayinsConfigurationSignature {
  return (payinsConfigurationId: string) =>
    universalGetPayinsConfiguration(
      payinsConfigurationId,
      // @ts-ignore
      { httpClient }
    );
}

interface GetPayinsConfigurationSignature {
  /**
   * Retrieves a PayinsConfiguration.
   * @param - ID of the PayinsConfiguration to retrieve.
   * @returns The requested PayinsConfiguration.
   */
  (payinsConfigurationId: string): Promise<
    NonNullablePaths<
      PayinsConfiguration,
      | `rules`
      | `rules.${number}.paymentMethodTypeId`
      | `rules.${number}.accountConnectionId`
      | `rules.${number}.merchantSelection.payinsEnabled`
      | `tags.privateTags.tagIds`,
      5
    >
  >;
}

/** @internal */
export function updatePayinsConfiguration(
  httpClient: HttpClient
): UpdatePayinsConfigurationSignature {
  return (
    _id: string,
    payinsConfiguration: NonNullablePaths<
      UpdatePayinsConfiguration,
      `revision`,
      2
    >
  ) =>
    universalUpdatePayinsConfiguration(
      _id,
      payinsConfiguration,
      // @ts-ignore
      { httpClient }
    );
}

interface UpdatePayinsConfigurationSignature {
  /**
   * Updates a PayinsConfiguration.
   *
   * Each time the PayinsConfiguration is updated,
   * `revision` increments by 1.
   * The current `revision` must be passed when updating the PayinsConfiguration.
   * This ensures you're working with the latest PayinsConfiguration
   * and prevents unintended overwrites.
   * @param - id
   * @returns Updated PayinsConfiguration.
   */
  (
    _id: string,
    payinsConfiguration: NonNullablePaths<
      UpdatePayinsConfiguration,
      `revision`,
      2
    >
  ): Promise<
    NonNullablePaths<
      PayinsConfiguration,
      | `rules`
      | `rules.${number}.paymentMethodTypeId`
      | `rules.${number}.accountConnectionId`
      | `rules.${number}.merchantSelection.payinsEnabled`
      | `tags.privateTags.tagIds`,
      5
    >
  >;
}

/** @internal */
export function queryPayinsConfigurations(
  httpClient: HttpClient
): QueryPayinsConfigurationsSignature {
  return () =>
    universalQueryPayinsConfigurations(
      // @ts-ignore
      { httpClient }
    );
}

interface QueryPayinsConfigurationsSignature {
  /**
   * Retrieves a list of PayinsConfigurations, given the provided [paging, filtering, and sorting][1].
   *
   * Up to 1,000 PayinsConfigurations can be returned per request.
   *
   * To learn how to query PayinsConfigurations, see [API Query Language][2].
   *
   * [1]: https://dev.wix.com/api/rest/getting-started/sorting-and-paging
   * [2]: https://dev.wix.com/api/rest/getting-started/api-query-language
   */
  (): PayinsConfigurationsQueryBuilder;
}

export function typedQueryPayinsConfigurations(
  httpClient: HttpClient
): TypedQueryPayinsConfigurationsSignature {
  return (query: PayinsConfigurationQuery) =>
    universalTypedQueryPayinsConfigurations(
      query,
      // @ts-ignore
      { httpClient }
    );
}

interface TypedQueryPayinsConfigurationsSignature {
  /** */
  (query: PayinsConfigurationQuery): Promise<
    NonNullablePaths<
      QueryPayinsConfigurationsResponse,
      `payinsConfigurations`,
      2
    >
  >;
}

/** @internal */
export function bulkUpdatePayinsConfigurationTags(
  httpClient: HttpClient
): BulkUpdatePayinsConfigurationTagsSignature {
  return (
    ids: string[],
    options?: NonNullablePaths<
      BulkUpdatePayinsConfigurationTagsOptions,
      `assignTags`,
      2
    >
  ) =>
    universalBulkUpdatePayinsConfigurationTags(
      ids,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkUpdatePayinsConfigurationTagsSignature {
  /**
   * Synchronously update tags on multiple PayinsConfigurations, by list of PayinsConfigurations ids
   * A tag that appears both in the list of assign and unassign tags, will be assigned
   * @param - List of PayinsConfigurations that their tags will update.
   */
  (
    ids: string[],
    options?: NonNullablePaths<
      BulkUpdatePayinsConfigurationTagsOptions,
      `assignTags`,
      2
    >
  ): Promise<
    NonNullablePaths<
      BulkUpdatePayinsConfigurationTagsResponse,
      | `results`
      | `results.${number}.itemMetadata.originalIndex`
      | `results.${number}.itemMetadata.success`
      | `results.${number}.itemMetadata.error.code`
      | `results.${number}.itemMetadata.error.description`
      | `bulkActionMetadata.totalSuccesses`
      | `bulkActionMetadata.totalFailures`
      | `bulkActionMetadata.undetailedFailures`,
      6
    >
  >;
}

/** @internal */
export function bulkUpdatePayinsConfigurationTagsByFilter(
  httpClient: HttpClient
): BulkUpdatePayinsConfigurationTagsByFilterSignature {
  return (
    filter: Record<string, any>,
    options?: NonNullablePaths<
      BulkUpdatePayinsConfigurationTagsByFilterOptions,
      `assignTags`,
      2
    >
  ) =>
    universalBulkUpdatePayinsConfigurationTagsByFilter(
      filter,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkUpdatePayinsConfigurationTagsByFilterSignature {
  /**
   * Asynchronously update tags on multiple PayinsConfigurations, by provided filter
   * An empty filter will update all PayinsConfigurations
   * A tag that appears both in the list of assign and unassign tags, will be assigned
   * @param - Filter
   */
  (
    filter: Record<string, any>,
    options?: NonNullablePaths<
      BulkUpdatePayinsConfigurationTagsByFilterOptions,
      `assignTags`,
      2
    >
  ): Promise<
    NonNullablePaths<
      BulkUpdatePayinsConfigurationTagsByFilterResponse,
      `jobId`,
      2
    >
  >;
}

export {
  ActionEvent,
  ApplicationError,
  BulkActionMetadata,
  BulkUpdatePayinsConfigurationTagsByFilterOptions,
  BulkUpdatePayinsConfigurationTagsByFilterRequest,
  BulkUpdatePayinsConfigurationTagsByFilterResponse,
  BulkUpdatePayinsConfigurationTagsOptions,
  BulkUpdatePayinsConfigurationTagsRequest,
  BulkUpdatePayinsConfigurationTagsResponse,
  BulkUpdatePayinsConfigurationTagsResult,
  CreatePayinsConfigurationRequest,
  CreatePayinsConfigurationResponse,
  CursorPaging,
  CursorPagingMetadata,
  CursorQuery,
  CursorQueryPagingMethodOneOf,
  Cursors,
  DomainEvent,
  DomainEventBodyOneOf,
  EntityCreatedEvent,
  EntityDeletedEvent,
  EntityUpdatedEvent,
  ExtendedFields,
  GetPayinsConfigurationRequest,
  GetPayinsConfigurationResponse,
  IdentificationData,
  IdentificationDataIdOneOf,
  ItemMetadata,
  MerchantSelection,
  MessageEnvelope,
  PayinsConfiguration,
  PayinsConfigurationQuerySpec,
  PayinsConfigurationsQueryBuilder,
  PayinsConfigurationsQueryResult,
  PayinsRule,
  QueryPayinsConfigurationsRequest,
  QueryPayinsConfigurationsResponse,
  RestoreInfo,
  SortOrder,
  Sorting,
  TagList,
  Tags,
  UpdatePayinsConfiguration,
  UpdatePayinsConfigurationRequest,
  UpdatePayinsConfigurationResponse,
  WebhookIdentityType,
  utils,
} from './payments-payins-configurations-v1-payins-configuration-payins-configurations.universal.js';
